/**
 * ConfirmDialog - Reusable confirmation dialog component
 * Uses Radix Dialog for proper focus management and accessibility
 */

import { useState, useCallback } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { clsx } from 'clsx';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'default';
  isLoading?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  isLoading: externalLoading,
}: ConfirmDialogProps) {
  const [internalLoading, setInternalLoading] = useState(false);
  const isLoading = externalLoading ?? internalLoading;

  const handleConfirm = useCallback(async () => {
    setInternalLoading(true);
    try {
      await onConfirm();
    } finally {
      setInternalLoading(false);
    }
  }, [onConfirm]);

  const buttonColors = {
    danger: 'bg-red-600 hover:bg-red-700',
    warning: 'bg-amber-600 hover:bg-amber-700',
    default: 'bg-primary-600 hover:bg-primary-700',
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={clsx(
            'fixed inset-0 z-[100] bg-black/50',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        />
        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-[100] -translate-x-1/2 -translate-y-1/2',
            'w-[calc(100%-2rem)] max-w-sm p-6 rounded-xl shadow-xl',
            'bg-white dark:bg-neutral-800',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
          )}
        >
          <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
            {title}
          </Dialog.Title>
          <Dialog.Description className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
            {description}
          </Dialog.Description>
          <div className="mt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={isLoading}
              className={`px-4 py-2 text-sm font-medium text-white ${buttonColors[variant]} rounded-lg disabled:opacity-50`}
            >
              {isLoading ? 'Please wait...' : confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Hook for easier use of ConfirmDialog with Promise-based API
 * Usage: const confirmed = await confirm({ title, description, ... });
 */
export function useConfirmDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<{
    title: string;
    description: string;
    confirmLabel?: string;
    variant?: 'danger' | 'warning' | 'default';
  } | null>(null);
  const [resolveRef, setResolveRef] = useState<((value: boolean) => void) | null>(null);

  const confirm = useCallback(
    (options: {
      title: string;
      description: string;
      confirmLabel?: string;
      variant?: 'danger' | 'warning' | 'default';
    }): Promise<boolean> => {
      return new Promise((resolve) => {
        setConfig(options);
        setResolveRef(() => resolve);
        setIsOpen(true);
      });
    },
    []
  );

  const close = useCallback(() => {
    setIsOpen(false);
    if (resolveRef) {
      resolveRef(false);
      setResolveRef(null);
    }
    setConfig(null);
  }, [resolveRef]);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    if (resolveRef) {
      resolveRef(true);
      setResolveRef(null);
    }
    setConfig(null);
  }, [resolveRef]);

  const dialogProps: ConfirmDialogProps | null = config
    ? {
        open: isOpen,
        onClose: close,
        onConfirm: handleConfirm,
        title: config.title,
        description: config.description,
        confirmLabel: config.confirmLabel || 'Delete',
        variant: config.variant || 'danger',
      }
    : null;

  return {
    confirm,
    close,
    isOpen,
    dialogProps,
    ConfirmDialogComponent: dialogProps ? (
      <ConfirmDialog {...dialogProps} />
    ) : null,
  };
}
